/**
 * Canonical vs stream sidebar stats for Analytics full-ride and segment views.
 * Chart lines remain sample-based; only sidebar summary numbers use these resolvers.
 */

import { metricStats } from "./metricStats";
import {
  formatRateSidebarValueFromChartSpeed,
  formatRateSidebarValueFromMs,
} from "./analyticsRate";
import {
  formatCadenceRpm,
  resolveMaxCadence,
  resolveMovingSpeedMs,
  resolveOverallCadence,
  resolvePedallingCadence,
} from "./motionMetrics";

export const SIDEBAR_SOURCE = {
  CANONICAL: "canonical",
  STREAM: "stream",
  SEGMENT: "segment",
};

function formatSpeedDisplay(ms, sport, unitSystem) {
  return formatRateSidebarValueFromMs(ms, sport, unitSystem);
}

function streamSidebarStats(chartData) {
  return {
    speed: metricStats(chartData, "speed_kmh", 1),
    cadence: metricStats(chartData, "cadence", 0),
  };
}

function resolveSpeedSidebar(activity, chartData, unitSystem) {
  const sport = activity?.sport || "";
  const stream = streamSidebarStats(chartData).speed;
  const canonicalMs = resolveMovingSpeedMs(activity);
  if (canonicalMs != null) {
    return {
      max:
        activity.max_speed_ms != null
          ? formatSpeedDisplay(activity.max_speed_ms, sport, unitSystem)
          : formatRateSidebarValueFromChartSpeed(stream.max, sport, unitSystem),
      avg: formatSpeedDisplay(canonicalMs, sport, unitSystem),
      source: SIDEBAR_SOURCE.CANONICAL,
    };
  }
  return {
    max: formatRateSidebarValueFromChartSpeed(stream.max, sport, unitSystem),
    avg: formatRateSidebarValueFromChartSpeed(stream.avg, sport, unitSystem),
    source: SIDEBAR_SOURCE.STREAM,
  };
}

function resolveCadenceSidebar(activity, chartData) {
  const stream = streamSidebarStats(chartData).cadence;
  const pedalling = resolvePedallingCadence(activity);
  const overall = resolveOverallCadence(activity);
  const maxCadence = resolveMaxCadence(activity);

  if (pedalling != null) {
    return {
      max: maxCadence != null ? formatCadenceRpm(maxCadence) : stream.max,
      avg: formatCadenceRpm(pedalling),
      overall: overall != null ? formatCadenceRpm(overall) : null,
      source: SIDEBAR_SOURCE.CANONICAL,
    };
  }

  return {
    max: stream.max,
    avg: stream.avg,
    overall: null,
    source: SIDEBAR_SOURCE.STREAM,
  };
}

/** Full-ride sidebar stats aligned with ActivitySummaryStats canonical fields. */
export function resolveFullRideSidebarStats(activity, chartData, unitSystem = "metric") {
  return {
    mode: "full",
    speed: resolveSpeedSidebar(activity, chartData, unitSystem),
    cadence: resolveCadenceSidebar(activity, chartData),
  };
}

/** Segment sidebar stats from computeSegmentStats, with stream fallback. */
export function resolveSegmentSidebarStats(
  segmentStats,
  chartData,
  unitSystem = "metric",
  sport = ""
) {
  const stream = streamSidebarStats(chartData);

  const speed = {
    max:
      segmentStats?.max_speed_ms != null
        ? formatSpeedDisplay(segmentStats.max_speed_ms, sport, unitSystem)
        : formatRateSidebarValueFromChartSpeed(stream.speed.max, sport, unitSystem),
    avg:
      segmentStats?.avg_speed_ms != null
        ? formatSpeedDisplay(segmentStats.avg_speed_ms, sport, unitSystem)
        : formatRateSidebarValueFromChartSpeed(stream.speed.avg, sport, unitSystem),
    source: SIDEBAR_SOURCE.SEGMENT,
  };

  const cadence = {
    max:
      segmentStats?.max_cadence != null
        ? Math.round(segmentStats.max_cadence)
        : stream.cadence.max,
    avg:
      segmentStats?.avg_cadence != null
        ? Math.round(segmentStats.avg_cadence)
        : stream.cadence.avg,
    overall: null,
    source: SIDEBAR_SOURCE.SEGMENT,
  };

  return {
    mode: "segment",
    speed,
    cadence,
  };
}

/** Pick full-ride or segment sidebar stats for AnalyticsView. */
export function resolveAnalyticsSidebarStats({
  activity,
  chartData,
  segmentStats,
  isFullRide,
  unitSystem = "metric",
}) {
  if (isFullRide) {
    return resolveFullRideSidebarStats(activity, chartData, unitSystem);
  }
  return resolveSegmentSidebarStats(segmentStats, chartData, unitSystem, activity?.sport || "");
}
